import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import InventoryNav from "../components/inventoryNav";
import "../css/constructionNewsFlash.css";

const API_BASE = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";

function parseLocalDate(input) {
  if (!input) return null;
  if (input instanceof Date) return input;

  const s = String(input).trim();

  // Handle pure date strings as LOCAL dates to avoid UTC shifting (e.g., "2025-09-30")
  // Matches "YYYY-MM-DD" optionally followed by time, but we only localize if it's DATE-ONLY.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dateOnlyMatch) {
    const [, y, m, d] = dateOnlyMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  }

  // Fallback to native parsing for other formats (with time / timezone)
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

const ConstructionNewsFlash = () => {
  const [newsRows, setNewsRows] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);

  // keep only approved & not-expired (include items that expire TODAY until 23:59:59.999 local)
  const isActiveRow = (row) => {
    const approved = String(row?.Approve || "").trim().toLowerCase() === "yes";
    if (!approved) return false;

    if (row?.Expired_Date) {
      const exp = parseLocalDate(row.Expired_Date);
      if (!exp) return false;
      const endOfDay = new Date(
        exp.getFullYear(),
        exp.getMonth(),
        exp.getDate(),
        23, 59, 59, 999
      );
      if (Date.now() > endOfDay.getTime()) return false; // after local end-of-day => expired
    }
    return true;
  };

  const normalizeRows = (rows = []) =>
    rows
      .filter(isActiveRow)
      .sort((a, b) => {
        const aDate =
          parseLocalDate(a.News_EntryDate) ||
          parseLocalDate(a.Expired_Date) ||
          new Date(0);
        const bDate =
          parseLocalDate(b.News_EntryDate) ||
          parseLocalDate(b.Expired_Date) ||
          new Date(0);
        return bDate - aDate; // newest first
      })
      .map((r, i) => ({
        id: r.SlNo ?? i,
        heading: r.Heading || "—",
        content: r.Message || "",
        description: r.Description || "",
        // Prefer entry date for display, fallback to expiry date
        date:
          parseLocalDate(r.News_EntryDate) ||
          parseLocalDate(r.Expired_Date) ||
          null,
      }));

  const newsData = useMemo(() => normalizeRows(newsRows), [newsRows]);
  const hasNews = newsData.length > 0;
  const active = hasNews ? newsData[Math.min(currentNewsIndex, newsData.length - 1)] : null;

  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoadingNews(true);
        const { data } = await axios.get(`${API_BASE}/flash_News_for_users`);
        setNewsRows(data?.success && Array.isArray(data.users) ? data.users : []);
      } catch {
        setNewsRows([]);
      } finally {
        setLoadingNews(false);
      }
    };
    loadNews();
  }, []);

  // Keep current index in range if list size changes
  useEffect(() => {
    if (currentNewsIndex >= newsData.length) {
      setCurrentNewsIndex(0);
    }
  }, [newsData.length, currentNewsIndex]);

  const formatDate = (value) => {
    const d = value instanceof Date ? value : parseLocalDate(value);
    return d
      ? d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
      : "";
  };

  return (
    <div id="admin-container">
      <InventoryNav />
      {/* NOTE: the cnf-light class scopes the new light theme and hard-resets old styles */}
      <div className="content-right cnf-surface cnf-light">
        <div className="cnf-grid">
          <header className="cnf-header">
            <h1>Flash News</h1>
          </header>

          <main className="cnf-main">
            <section className="cnf-card">
              {loadingNews ? (
                <div className="cnf-skeleton">
                  <div className="skl-line w60" />
                  <div className="skl-line w90" />
                  <div className="skl-line w85" />
                </div>
              ) : !hasNews ? (
                <div className="cnf-empty">
                  <h2>No active news</h2>
                  <p>There are currently no approved or non-expired items.</p>
                </div>
              ) : (
                <>
                  {active?.date && <span className="cnf-date">{formatDate(active.date)}</span>}
                  <h2 className="cnf-heading">{active.heading}</h2>
                  {active.content && <p className="cnf-content">{active.content}</p>}
                  {active.description && (
                    <div className="cnf-desc">
                      <span role="img" aria-label="note">📋</span>
                      <p>{active.description}</p>
                    </div>
                  )}
                </>
              )}
            </section>

            <aside className="cnf-aside">
              <div className="cnf-aside-head">
                <span>Stories</span>
                <span className="cnf-count">{newsData.length || 0}</span>
              </div>
              <div className="cnf-list">
                {loadingNews ? (
                  <>
                    <div className="cnf-item skl" />
                    <div className="cnf-item skl" />
                  </>
                ) : hasNews ? (
                  newsData.map((it, idx) => (
                    <button
                      key={it.id ?? idx}
                      className={`cnf-item ${idx === currentNewsIndex ? "is-active" : ""}`}
                      onClick={() => setCurrentNewsIndex(idx)}
                    >
                      <div className="cnf-item-title">{it.heading}</div>
                      {it.date && <div className="cnf-item-date">{formatDate(it.date)}</div>}
                    </button>
                  ))
                ) : (
                  <div className="cnf-item-empty">No stories</div>
                )}
              </div>
            </aside>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ConstructionNewsFlash;
